import SegmentService, { EventGroup, EventTypeInteraction } from "module/analytics/service/SegmentService"
import { TrackingProps } from "module/analytics/types"

import {
    Box,
    BoxProps,
    HStack,
    StackDivider,
    StackProps,
    Text,
    UseRadioGroupProps,
    useColorModeValue,
    useRadio,
    useRadioGroup,
} from "@chakra-ui/react"
import React, { useCallback } from "react"

interface PerpRadioButtonProps {
    children: React.ReactNode
    styleProps?: BoxProps
}

function RadioButton({ children, styleProps, ...radioProps }: PerpRadioButtonProps) {
    const { getInputProps, getCheckboxProps } = useRadio(radioProps)

    const input = getInputProps()
    const checkbox = getCheckboxProps()

    const colorBgSelected = useColorModeValue("gray.300", "gray.300")
    const colorTextSelected = useColorModeValue("gray.900", "gray.900")
    const colorBorderSelected = useColorModeValue("gray.200", "gray.200")

    return (
        <Box as="label">
            <input {...input} />
            <Box
                {...checkbox}
                cursor="pointer"
                userSelect="none"
                px={3}
                py={1}
                _checked={{
                    backgroundColor: colorBgSelected,
                    textColor: colorTextSelected,
                    borderColor: colorBorderSelected,
                }}
                {...styleProps}
            >
                <Text variant="label"> {children}</Text>
            </Box>
        </Box>
    )
}

export interface PerpRadioGroupProps extends UseRadioGroupProps {
    options: { value: string; label: string }[]
    containerProps?: StackProps
    optionStyleProps?: BoxProps
    trackingProps: TrackingProps
}

export const PerpRadioGroup = ({
    options,
    onChange,
    containerProps,
    optionStyleProps,
    trackingProps,
    ...props
}: PerpRadioGroupProps) => {
    const handleChange = useCallback<(nextValue: string) => void>(
        value => {
            onChange && onChange(value)

            SegmentService.track(EventGroup.INTERACTION, {
                eventType: EventTypeInteraction.RADIO_GROUP_SELECTED,
                ...trackingProps,
                payload: { option: value, ...trackingProps.payload },
            })
        },
        [onChange, trackingProps],
    )

    const { getRootProps, getRadioProps } = useRadioGroup({
        onChange: handleChange,
        ...props,
    })

    const groupProps = getRootProps()

    return (
        <HStack
            borderRadius="full"
            overflow="hidden"
            w="auto"
            spacing={0}
            bg="whiteAlpha.200"
            divider={<StackDivider bg="blackAlpha.900" />}
            {...containerProps}
            {...groupProps}
        >
            {options.map(({ value, label }) => {
                const radioProps = getRadioProps({ value })
                return (
                    <RadioButton key={value} styleProps={optionStyleProps} {...radioProps}>
                        {label}
                    </RadioButton>
                )
            })}
        </HStack>
    )
}
